import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}
interface State {
  error: Error | null;
}

/** App-level error boundary. Catches render errors so the whole SPA never blanks out. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Hook point for an error-reporting service later.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-surface-container-low px-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-danger/10 text-danger">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-headline-md text-on-surface">Something went wrong</h1>
          <p className="mt-1 max-w-md text-data-md text-on-surface-variant">
            An unexpected error occurred while rendering this page.
          </p>
        </div>
        <Button onClick={() => window.location.reload()}>Reload app</Button>
      </div>
    );
  }
}
